import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle, Siren } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { getNotifications, markAllNotificationsRead } from "../lib/api";

/**
 * Notification bell + dropdown. Polls `/api/notifications` every 60s for
 * authed users, shows unread count as a small badge, and lets the user
 * mark all as read. Clicking the bell toggles the dropdown. Rendered in
 * the header alongside `AuthPill` — hidden entirely when logged out.
 */
export default function NotificationBell() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [data, setData] = useState({ items: [], unread_count: 0 });
    const [loading, setLoading] = useState(false);
    const panelRef = useRef(null);

    const refresh = async () => {
        if (!user) return;
        try {
            const d = await getNotifications(false, 10);
            setData(d);
        } catch (err) {
            // non-critical — bell will retry next poll
            console.warn("NotificationBell: poll failed", err);
        }
    };

    useEffect(() => {
        if (!user) return undefined;
        refresh();
        const t = setInterval(refresh, 60000);
        return () => clearInterval(t);
    }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Outside-click close
    useEffect(() => {
        if (!open) return undefined;
        const onClick = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    if (!user) return null;

    const onMarkAll = async () => {
        setLoading(true);
        try {
            await markAllNotificationsRead();
            await refresh();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative" ref={panelRef}>
            <button
                type="button"
                onClick={() => { setOpen((v) => !v); if (!open) refresh(); }}
                className="relative p-1.5 border border-white/10 hover:border-fa-cream/40 text-white/70 hover:text-fa-cream transition-colors"
                data-testid="notification-bell"
                title={data.unread_count > 0 ? `${data.unread_count} unread` : "Notifications"}
            >
                <Bell size={16} weight={data.unread_count > 0 ? "fill" : "bold"} />
                {data.unread_count > 0 && (
                    <span
                        className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 bg-red-500 text-white text-[9px] font-mono font-bold leading-[14px] text-center border border-black"
                        data-testid="notification-badge"
                    >
                        {data.unread_count > 9 ? "9+" : data.unread_count}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 top-full mt-2 w-[340px] bg-[#0b0b0b] border border-fa-violet/30 shadow-[0_8px_32px_rgba(124,77,255,0.25)] z-50"
                    data-testid="notification-panel"
                >
                    <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cream">
                            Notifications
                        </div>
                        {data.unread_count > 0 && (
                            <button
                                type="button"
                                onClick={onMarkAll}
                                disabled={loading}
                                className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest text-white/60 hover:text-fa-cream transition-colors disabled:opacity-50"
                                data-testid="notification-read-all"
                            >
                                <CheckCircle size={10} weight="bold" />
                                Mark all read
                            </button>
                        )}
                    </div>
                    <div className="max-h-[420px] overflow-y-auto">
                        {data.items.length === 0 ? (
                            <div className="p-6 text-center font-mono text-[10px] uppercase tracking-widest text-white/40">
                                No notifications yet
                            </div>
                        ) : (
                            data.items.map((it) => {
                                const clickable = it.player_id != null;
                                return (
                                    <div
                                        key={`${it.user_id}-${it.created_at}`}
                                        onClick={() => { if (clickable) { setOpen(false); navigate(`/players/${it.player_id}`); } }}
                                        className={`px-3 py-2 border-b border-white/5 ${it.read ? "opacity-60" : "bg-fa-violet/5"} ${clickable ? "cursor-pointer hover:bg-white/5 hover:opacity-100" : ""}`}
                                        data-testid="notification-item"
                                    >
                                        <div className="font-mono text-[11px] text-white leading-snug">
                                            {it.text}
                                        </div>
                                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-0.5 flex items-center gap-2">
                                            {formatRelativeTime(it.created_at)}
                                            {clickable && <span className="text-fa-mint/70">profil hráče →</span>}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => { setOpen(false); navigate("/hlidac"); }}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 border-t border-white/10 font-mono text-[9px] uppercase tracking-widest hover:bg-[#FF8800]/10 transition-colors"
                        style={{ color: "#FF8800" }}
                        data-testid="notification-full-feed"
                    >
                        <Siren size={11} weight="fill" /> Celý feed v Hlídači
                    </button>
                </div>
            )}
        </div>
    );
}

function formatRelativeTime(iso) {
    if (!iso) return "";
    try {
        const then = new Date(iso).getTime();
        const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
        if (diffSec < 60) return `${diffSec}s ago`;
        if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
        if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
        return `${Math.floor(diffSec / 86400)}d ago`;
    } catch {
        return "";
    }
}
